import * as R from "ramda";
import { Dispatch } from "redux";

import { fetchForReduxPromiseMiddleware } from "../src/fetch_for_redux";

export const SET_ENDPOINT = "list/SET_ENDPOINT";

export const READ_PAGE = "list/READ_PAGE";
export const READ_PAGE_PENDING = "list/READ_PAGE_PENDING";
export const READ_PAGE_FULFILLED = "list/READ_PAGE_FULFILLED";
export const READ_PAGE_REJECTED = "list/READ_PAGE_REJECTED";
export const RELOAD_LIST = "list/RELOAD_LIST";
export const RELOAD_LIST_PENDING = "list/RELOAD_LIST_PENDING";
export const RELOAD_LIST_FULFILLED = "list/RELOAD_LIST_FULFILLED";
export const RELOAD_LIST_REJECTED = "list/RELOAD_LIST_REJECTED";

export const RESET_PAGES = "list/RESET_PAGES";

export const ADD_FILTER = "list/ADD_FILTER";
export const REMOVE_FILTER = "list/REMOVE_FILTER";

export const SET_SEARCH = "list/SET_SEARCH";
export const RESET_SEARCH = "list/RESET_SEARCH";

export const SET_SORT = "list/SET_SORT";
export const RESET_SORT = "list/RESET_SORT";

export const SET_SELECTED = "list/SET_SELECTED";
export const RESET_SELECTED = "list/RESET_SELECTED";
export const ADD_SELECTED = "list/ADD_SELECTED";
export const REMOVE_SELECTED = "list/REMOVE_SELECTED";

export const SET_ITEMS_PER_PAGE = "list/SET_ITEMS_PER_PAGE";

export const SET_HISTORY_KEY_VALUES = "list/SET_HISTORY_KEY_VALUES";

// const PENDING = '_PENDING'
// const FULFILLED = '_FULFILLED'
// const REJECTED = '_REJECTED'

export function listsReducer<Item extends {id: number}>(state: Record<string, ListState<Item>>={}, action: ListAction<Item>) {
  if (action && action.meta?.listID) {
    return {...state,
      [action.meta.listID]: listReducer<Item>(state[action.meta.listID], action),
    };
  } else {
    return state;
  }
}

export function getListFromStore<Item extends {id: number}>(store: GlobalStore, listID: string): ListState<Item> {
  return store.lists[listID] || DEFAULT_STATE;
}

const DEFAULT_STATE = {
  isSyncing: false,
  page: 0,
  per_page: 24,
  selected: [],
};

function listReducer<Item extends {id: number}>(state: ListState<Item>=DEFAULT_STATE, action: ListAction<Item>): ListState<Item> {
  switch (action.type) {
    case SET_ENDPOINT:
      return {...state,
        endpoint: action.payload.endpoint,
      };
    case READ_PAGE_PENDING:
    case RELOAD_LIST_PENDING:
      return {...state,
        isSyncing: true,
      };
    case READ_PAGE_FULFILLED:
      return {...state,
        isSyncing: false,
        lastSyncedAt: Date.now(),
        syncError: undefined,
        listData: (state.listData || []).concat(action.payload.data),
        page: state.page + 1,
        lastPageLength: action.payload.data.length,
      };
    case RELOAD_LIST_FULFILLED:
      return {...state,
        isSyncing: false,
        lastSyncedAt: Date.now(),
        syncError: undefined,
        listData: action.payload.data,
        page: state.page,
        lastPageLength: Math.max(0, action.payload.data.length - (state.page * state.per_page)),
      };
    case READ_PAGE_REJECTED:
    case RELOAD_LIST_REJECTED:
      return {...state,
        isSyncing: false,
        syncError: action.payload.message,
      };
    case RESET_PAGES:
      return {...state,
        isSyncing: false,
        lastSyncedAt: undefined,
        syncError: undefined,
        listData: undefined,
        page: 0,
        lastPageLength: undefined,
      };
    case ADD_FILTER:
      return {...state,
        activeFilters: {...state.activeFilters,
          [action.payload.filterID]: action.payload.filter,
        },
      };
    case REMOVE_FILTER:
      return {...state,
        activeFilters: state.activeFilters ? R.dissoc(action.payload.filterID, state.activeFilters) : {},
      };
    case SET_SEARCH:
      return {...state,
        search: action.payload.search,
      };
    case RESET_SEARCH:
      return R.omit(["search"], state);
    case SET_SORT:
      return {...state,
        sort_by: action.payload.sort_by,
        sort_order: action.payload.sort_order,
      };
    case RESET_SORT:
      return R.omit(["sort_by", "sort_order"], state);
    case SET_SELECTED:
      return {...state,
        selected: action.payload.selected,
      };
    case RESET_SELECTED:
      return {...state,
        selected: [],
      };
    case ADD_SELECTED:
      return {...state,
        selected: R.unionWith((a, b) => a.id === b.id, state.selected, action.payload.addSelected),
      };
    case REMOVE_SELECTED:
      return {...state,
        selected: R.differenceWith((a, b) => a.id === b.id, state.selected, action.payload.removeSelected),
      };
    case SET_ITEMS_PER_PAGE:
      return {...state,
        per_page: action.payload.per_page,
      };
    case SET_HISTORY_KEY_VALUES:
      return {...state,
        ...action.payload.history_key_values,
      };
    default:
      return state;
  }
}

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

export function setEndpoint(listID: string, endpoint: string) {
  return {
    type: SET_ENDPOINT,
    meta: {listID},
    payload: {endpoint},
  };
}


function listDataFromStore<Item extends {id: number}>(listStore: ListState<Item>=DEFAULT_STATE) {
  const data: any = R.pick([ // TODO: Get rid of any
      "search",
      "sort_order",
      "sort_by",
      "page",
      "per_page",
    ] as const,
    listStore,
  );

  if (listStore.activeFilters && Object.values(listStore.activeFilters).length > 0) {
    data.where = JSON.stringify(Object.values(listStore.activeFilters));
  }

  return data;
}

export function readListPageWithData(listID: string, endpoint: string, data: ReturnType<typeof listDataFromStore>) {
  return {
    type: READ_PAGE,
    meta: {listID},
    payload: fetchForReduxPromiseMiddleware(`${endpoint}?${new URLSearchParams(data)}`),
  };
}

export function readListPage(listID: string) {
  return (dispatch: Dispatch, getState: () => GlobalStore) => {
    const state = getState();
    const list = state.lists[listID];
    const data = listDataFromStore(list);

    const endpoint = list.endpoint;

    dispatch(readListPageWithData(listID, endpoint, data));
  };
}

export function reloadListWithData(listID: string, endpoint: string, data: ReturnType<typeof listDataFromStore>) {
  return {
    type: RELOAD_LIST,
    meta: {listID},
    payload: fetchForReduxPromiseMiddleware(`${endpoint}?${new URLSearchParams(data)}`),
  };
}

export function reloadList(listID: string) {
  return (dispatch: Dispatch, getState: () => GlobalStore) => {
    const state = getState();
    const list = state.lists[listID];
    let data = listDataFromStore(list);

    data = {
      ...data,
      page: 0,
      per_page: list.per_page * list.page,
    };

    const endpoint = list.endpoint;

    dispatch(reloadListWithData(listID, endpoint, data));
  };
}

export function resetListPages(listID: string) {
  return {
    type: RESET_PAGES,
    meta: {listID},
  };
}

export function addListFilter(listID: string, filterID: string, filter: Filter) {
  return {
    type: ADD_FILTER,
    meta: {listID},
    payload: {filterID, filter},
  };
}

export function removeListFilter(listID: string, filterID: string) {
  return {
    type: REMOVE_FILTER,
    meta: {listID},
    payload: {filterID},
  };
}

export function setListSearch(listID: string, search: string) {
  return {
    type: SET_SEARCH,
    meta: {listID},
    payload: {search},
  };
}

export function resetListSearch(listID: string) {
  return {
    type: RESET_SEARCH,
    meta: {listID},
  };
}

export function setListSort<Item extends {id: number}>(listID: string, sort_by: keyof Item, sort_order: SortOrder) {
  return {
    type: SET_SORT,
    meta: {listID},
    payload: {sort_by, sort_order},
  };
}

export function resetListSort(listID: string) {
  return {
    type: RESET_SORT,
    meta: {listID},
  };
}

export function setListSelected<Item extends {id: number}>(listID: string, selected: Array<Item>) {
  return {
    type: SET_SELECTED,
    meta: {listID},
    payload: {selected},
  };
}

export function resetListSelected(listID: string) {
  return {
    type: RESET_SELECTED,
    meta: {listID},
  };
}

export function addListSelected<Item extends {id: number}>(listID: string, addSelected: Array<Item>) {
  return {
    type: ADD_SELECTED,
    meta: {listID},
    payload: {addSelected},
  };
}

export function removeListSelected<Item extends {id: number}>(listID: string, removeSelected: Array<Item>) {
  return {
    type: REMOVE_SELECTED,
    meta: {listID},
    payload: {removeSelected},
  };
}

export function setListItemsPerPage(listID: string, per_page: number) {
  return {
    type: SET_ITEMS_PER_PAGE,
    meta: {listID},
    payload: {per_page},
  };
}

export function setListHistoryKeyValues<Item extends {id: number}>(listID: string, history_key_values: Partial<ListState<Item>>) {
  return {
    type: SET_HISTORY_KEY_VALUES,
    meta: {listID},
    payload: {history_key_values},
  };
}
